import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Melody Trainer v1 timing lexicon - 13 entries, authored by Bowie in the Epic design §10 and
 * transcribed verbatim.
 *
 * Every entry maps to exactly one `scoreTiming` verdict. This is the second tool in the suite
 * whose output is a MEASUREMENT OF THE USER, and a surface that assembled its own prose could tell
 * a musician something no one wrote and no one reviewed - so it does not assemble one, and the
 * only sentences a player reads about their timing are these.
 *
 * `{{ms}}` is unsigned integer milliseconds - the direction lives in the entry id, not in the sign
 * - and `{{r}}` is the feel-match correlation to two decimals. Both interpolate at the use site
 * through `interpolateTokens`, which strips an unfilled token rather than leaking the raw
 * placeholder.
 *
 * The two cross-link entries live in `lexicon-crosslink-entries.ts` with the rest of the
 * cross-tool semantics.
 *
 * Copy rules applied by the author: beginner never says "ms" or "latency"; intermediate names the
 * number; advanced states the rule. "Rushing" and "dragging" are the musician's words and appear
 * from intermediate up, with beginner getting the plain direction. `timing-feel-straight` is never
 * a judgement - playing straight is a choice, and the sentence only reports that the reference
 * leans. "Pocket" appears at advanced only.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §10
 */
export const TIMING_LEXICON_ENTRIES: LexiconEntry[] = [
  // ───────── Per-note bands (§6.2) ─────────
  {
    id: 'timing-on',
    category: 'timing',
    displayName: 'On the beat',
    displayNameShort: 'On',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Right on the beat.',
      intermediate: 'On the beat — within 25 ms.',
      advanced: 'On — ≤ 25 ms.',
    },
    tagLabel: { beginner: 'on', intermediate: 'on', advanced: 'on' },
  },
  {
    id: 'timing-close-early',
    category: 'timing',
    displayName: 'A touch early',
    displayNameShort: 'Close early',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'A touch early.',
      intermediate: 'Slightly early — {{ms}} ms ahead.',
      advanced: '−{{ms}} ms, in the pocket.',
    },
    tagLabel: { beginner: 'a bit early', intermediate: 'early', advanced: '−{{ms}} ms' },
  },
  {
    id: 'timing-close-late',
    category: 'timing',
    displayName: 'A touch late',
    displayNameShort: 'Close late',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'A touch late.',
      intermediate: 'Slightly late — {{ms}} ms behind.',
      advanced: '+{{ms}} ms, in the pocket.',
    },
    tagLabel: { beginner: 'a bit late', intermediate: 'late', advanced: '+{{ms}} ms' },
  },
  {
    id: 'timing-off-early',
    category: 'timing',
    displayName: 'Early',
    displayNameShort: 'Early',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Early.',
      intermediate: 'Early — {{ms}} ms ahead of the beat.',
      advanced: '−{{ms}} ms, past the pocket.',
    },
    tagLabel: { beginner: 'early', intermediate: 'early', advanced: '−{{ms}} ms' },
  },
  {
    id: 'timing-off-late',
    category: 'timing',
    displayName: 'Late',
    displayNameShort: 'Late',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Late.',
      intermediate: 'Late — {{ms}} ms behind the beat.',
      advanced: '+{{ms}} ms, past the pocket.',
    },
    tagLabel: { beginner: 'late', intermediate: 'late', advanced: '+{{ms}} ms' },
  },
  {
    id: 'timing-wrong-place',
    category: 'timing',
    displayName: 'Wrong place',
    displayNameShort: 'Displaced',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'That note landed in the wrong place.',
      intermediate: 'Displaced — {{ms}} ms, a different beat position.',
      advanced: '|{{ms}}| > 120 ms: moved to another slot.',
    },
    tagLabel: { beginner: 'wrong place', intermediate: 'displaced', advanced: 'displaced' },
  },

  // ───────── Whole-phrase tendency (§6.2) ─────────
  {
    id: 'timing-rushing',
    category: 'timing',
    displayName: 'Rushing',
    displayNameShort: 'Rushing',
    pedagogicalLevel: 2,
    copy: {
      beginner: "You're pushing ahead of the beat overall.",
      intermediate: 'Rushing — {{ms}} ms ahead on average.',
      advanced: 'Mean −{{ms}} ms: rushing.',
    },
    tagLabel: { beginner: 'pushing', intermediate: 'rushing', advanced: '−{{ms}} ms mean' },
  },
  {
    id: 'timing-dragging',
    category: 'timing',
    displayName: 'Dragging',
    displayNameShort: 'Dragging',
    pedagogicalLevel: 2,
    copy: {
      beginner: "You're sitting behind the beat overall.",
      intermediate: 'Dragging — {{ms}} ms behind on average.',
      advanced: 'Mean +{{ms}} ms: dragging.',
    },
    tagLabel: { beginner: 'behind', intermediate: 'dragging', advanced: '+{{ms}} ms mean' },
  },
  {
    id: 'timing-even',
    category: 'timing',
    displayName: 'Even',
    displayNameShort: 'Even',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Your timing was even.',
      intermediate: 'Even — no lean ahead or behind.',
      advanced: 'Mean within ±25 ms.',
    },
    tagLabel: { beginner: 'even', intermediate: 'even', advanced: 'even' },
  },

  // ───────── Feel match (§6.2) ─────────
  {
    id: 'timing-feel-matched',
    category: 'timing',
    displayName: 'Feel matched',
    displayNameShort: 'Feel',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'You caught the feel.',
      intermediate: "You matched the melody's push and pull.",
      advanced: 'Feel match r = {{r}}.',
    },
    tagLabel: { beginner: 'feel', intermediate: 'feel matched', advanced: 'r = {{r}}' },
  },
  {
    id: 'timing-feel-straight',
    category: 'timing',
    displayName: 'Played straight',
    displayNameShort: 'Straight',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'You played it straight — the melody leans a little.',
      intermediate: 'The melody pushes and pulls; you played it on the grid.',
      advanced: 'Feel match r = {{r}}; reference has offsets.',
    },
    tagLabel: { beginner: 'straight', intermediate: 'played straight', advanced: 'r = {{r}}' },
  },

  // ───────── Latency calibration (§6.3) ─────────
  {
    id: 'timing-calibrate-first',
    category: 'timing',
    displayName: 'Calibrate input latency',
    displayNameShort: 'Calibrate',
    pedagogicalLevel: 1,
    copy: {
      beginner: "Let's measure your setup's delay first — tap along with eight clicks.",
      intermediate: 'Calibrate input latency before timing is scored — eight clicks, tap each.',
      advanced: 'Latency uncalibrated; scored timing would absorb the device delay.',
    },
    tagLabel: { beginner: 'measure delay', intermediate: 'calibrate', advanced: 'uncalibrated' },
  },
  {
    id: 'timing-calibrated',
    category: 'timing',
    displayName: 'Latency measured',
    displayNameShort: 'Calibrated',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Delay measured: {{ms}} ms.',
      intermediate: 'Input latency {{ms}} ms — timing is now scored against it.',
      advanced: 'inputLatencyMs = {{ms}}, median of 6.',
    },
    tagLabel: { beginner: 'measured', intermediate: 'calibrated', advanced: '{{ms}} ms' },
  },

  // ───────── Tool intro ─────────
  {
    id: 'timing-intro',
    category: 'timing',
    displayName: 'Melody Trainer',
    displayNameShort: 'Practice',
    pedagogicalLevel: 1,
    copy: {
      beginner: "Hear it, then sing or play it back — we'll check the notes and the timing.",
      intermediate: 'Reproduce the melody; pitch and timing are scored on every note.',
      advanced: 'Reproduction training: pitch (VocalLib) + timing (onset error vs. the melody with its feel).',
    },
    tagLabel: { beginner: 'practice', intermediate: 'practice', advanced: 'practice' },
  },
]
